import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Heart,
  MapPin,
  Radio,
  Share2,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';

import { OTHER_MARKET_SLUG } from '../../config/launchMarkets';
import {
  readStoredReferralCode,
  sanitizeReferralCode,
  storeReferralCode,
} from '../../config/launchAccess';
import TurnstileWidget from './TurnstileWidget';

const EMPTY_FORM = {
  firstName: '',
  email: '',
  customCity: '',
  gender: '',
  ageBracket: '',
  consent: false,
  website: '',
};

const GENDER_OPTIONS = [
  { value: 'woman', label: 'Woman' },
  { value: 'man', label: 'Man' },
  { value: 'self-describe', label: 'Prefer to self-describe' },
];

const AGE_BRACKET_OPTIONS = ['18-24', '25-29', '30-35', '36-45', '46+'];

const FOUNDING_MEMBER_GOAL = 3;

function WhatsAppIcon({ size = 17 }) {
  return (
    <svg
      aria-hidden="true"
      fill="currentColor"
      height={size}
      viewBox="0 0 24 24"
      width={size}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

function validateForm(form, city) {
  if (!form.firstName.trim() || form.firstName.trim().length > 60) {
    return 'Please enter your first name.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    return 'Please enter a valid email address.';
  }
  if (!city) return 'Please choose your city.';
  if (city === OTHER_MARKET_SLUG && !form.customCity.trim()) {
    return 'Tell us which city you want Hebrew Hearts to launch in.';
  }
  if (!GENDER_OPTIONS.some((option) => option.value === form.gender)) {
    return 'Please tell us how you identify.';
  }
  if (!AGE_BRACKET_OPTIONS.includes(form.ageBracket)) {
    return 'Please choose your age range.';
  }
  if (!form.consent) {
    return 'Please agree to receive launch and early-access updates.';
  }
  return '';
}

export default function PreLaunch({
  markets,
  selectedMarket,
  selectedSlug,
  onCityChange,
  launchStatusLoading,
}) {
  const location = useLocation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [city, setCity] = useState(selectedSlug || selectedMarket?.slug || '');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [signup, setSignup] = useState(null);
  const [referralCount, setReferralCount] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (selectedSlug) setCity(selectedSlug);
  }, [selectedSlug]);

  // Remember who sent this visitor, the same way the preferred city persists,
  // so the referral survives navigation before they actually sign up.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = sanitizeReferralCode(params.get('ref'));
    if (ref) storeReferralCode(ref);
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !signup?.referralCode) return undefined;

    let cancelled = false;
    fetch(`/api/waitlist-status?code=${encodeURIComponent(signup.referralCode)}`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data && Number.isInteger(data.referralCount)) {
          setReferralCount(data.referralCount);
        }
      })
      .catch(() => {
        // Progress is a nice-to-have; the share panel still works without it.
      });
    return () => {
      cancelled = true;
    };
  }, [status.type, signup]);

  useEffect(() => {
    if (location.hash === '#join') {
      window.requestAnimationFrame(() => {
        document.getElementById('join')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [location.hash]);

  const launchedMarkets = useMemo(
    () => markets.filter((market) => market.status === 'launched'),
    [markets]
  );
  const buildingMarkets = useMemo(
    () => markets.filter((market) => market.status !== 'launched'),
    [markets]
  );

  const selectedCityName = city === OTHER_MARKET_SLUG
    ? form.customCity.trim() || 'your city'
    : markets.find((market) => market.slug === city)?.name || 'your city';

  const updateField = (field) => (event) => {
    const value = field === 'consent' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    if (status.type === 'error') setStatus({ type: 'idle', message: '' });
  };

  const handleCitySelect = (event) => {
    const value = event.target.value;
    setCity(value);
    setStatus({ type: 'idle', message: '' });
    onCityChange(value);
  };

  const joinCity = (slug) => {
    setCity(slug);
    setStatus({ type: 'idle', message: '' });
    onCityChange(slug);
    window.requestAnimationFrame(() => {
      document.getElementById('join')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  const handleTurnstileUnavailable = useCallback(() => setTurnstileToken(''), []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm(form, city);
    if (validationError) {
      setStatus({ type: 'error', message: validationError });
      return;
    }

    setStatus({ type: 'submitting', message: '' });
    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          email: form.email.trim(),
          city,
          customCity: city === OTHER_MARKET_SLUG ? form.customCity.trim() : '',
          gender: form.gender,
          ageBracket: form.ageBracket,
          ref: readStoredReferralCode(),
          consent: form.consent,
          website: form.website,
          turnstileToken,
          source: 'prelaunch-homepage',
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'We could not save your place right now.');

      setForm(EMPTY_FORM);
      setTurnstileToken('');
      setTurnstileKey((key) => key + 1);
      setSignup({
        cityName: selectedCityName,
        referralCode: sanitizeReferralCode(result.referralCode),
        position: Number.isInteger(result.position) && result.position > 0 ? result.position : null,
        shareUrl: typeof result.shareUrl === 'string' ? result.shareUrl : '',
      });
      setReferralCount(null);
      setCopied(false);
      setStatus({
        type: 'success',
        message: `You’re on the ${selectedCityName} list.`,
      });
      if (typeof window !== 'undefined' && window.posthog?.capture) {
        window.posthog.capture('waitlist_joined', {
          city,
          gender: form.gender,
          age_bracket: form.ageBracket,
          position: Number.isInteger(result.position) ? result.position : null,
          referred: Boolean(readStoredReferralCode()),
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again.',
      });
    }
  };

  const resetForAnotherPerson = () => {
    setForm(EMPTY_FORM);
    setTurnstileToken('');
    setTurnstileKey((key) => key + 1);
    setSignup(null);
    setReferralCount(null);
    setCopied(false);
    setStatus({ type: 'idle', message: '' });
  };

  const shareMessage = signup
    ? `Hey! I just joined the Hebrew Hearts founding list for ${signup.cityName} — modern Jewish dating launching city by city (@hebrewheartsapp). Join through my link so we’re both in from day one:`
    : '';

  const copyShareUrl = async () => {
    if (!signup?.shareUrl) return;
    try {
      await navigator.clipboard.writeText(signup.shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      // Clipboard blocked; the read-only input still allows manual copying.
    }
  };

  const openNativeShare = () => {
    if (!signup?.shareUrl || !navigator.share) return;
    navigator
      .share({ title: 'Hebrew Hearts', text: shareMessage, url: signup.shareUrl })
      .catch(() => {
        // The user dismissed the share sheet; nothing to do.
      });
  };

  return (
    <main className="prelaunch" data-testid="prelaunch-page">
      <section className="prelaunch-hero" aria-labelledby="prelaunch-title">
        <div className="prelaunch-ambient prelaunch-ambient--one" aria-hidden="true" />
        <div className="prelaunch-ambient prelaunch-ambient--two" aria-hidden="true" />

        <div className="prelaunch-hero__copy">
          <p className="prelaunch-eyebrow">
            <span className="prelaunch-eyebrow__signal"><Radio size={13} /></span>
            Now building founding communities
          </p>
          <h1 id="prelaunch-title">
            Start matching<br />
            <em>in your city.</em>
          </h1>
          <p className="prelaunch-hero__lede">
            Hebrew Hearts is where traditional Jewish dating meets modern technology. We’re opening one
            city at a time—when your local community is ready, you’ll be first through the door.
          </p>

          <div className="prelaunch-trust-row" aria-label="What to expect">
            <span><ShieldCheck size={17} /> Private by design</span>
            <span><Sparkles size={17} /> Thoughtful matching</span>
            <span><MapPin size={17} /> Local first</span>
          </div>
        </div>

        <div className="prelaunch-card-wrap" id="join">
          <div className="prelaunch-orbit prelaunch-orbit--one" aria-hidden="true">♥</div>
          <div className="prelaunch-orbit prelaunch-orbit--two" aria-hidden="true">✦</div>
          <form className="prelaunch-card" onSubmit={handleSubmit} noValidate>
            {status.type === 'success' ? (
              <div className="prelaunch-success prelaunch-success--share" role="status">
                <span className="prelaunch-success__icon"><CheckCircle2 size={34} /></span>
                <p className="prelaunch-card__kicker">Your place is saved</p>
                <h2>
                  {signup?.position
                    ? `You’re #${signup.position} in ${signup.cityName}`
                    : status.message}
                </h2>
                <p className="prelaunch-success__offer">
                  Refer <strong>{FOUNDING_MEMBER_GOAL} friends</strong> who join and you’ll be a{' '}
                  <strong>Founding Member</strong> — first cohort access plus 3 months of
                  free premium.
                </p>

                {referralCount !== null && (
                  <p className="prelaunch-referral-progress" aria-live="polite">
                    <span className="prelaunch-referral-progress__dots" aria-hidden="true">
                      {Array.from({ length: FOUNDING_MEMBER_GOAL }, (_, index) => (
                        <span
                          key={index}
                          className={
                            index < Math.min(referralCount, FOUNDING_MEMBER_GOAL)
                              ? 'is-filled'
                              : ''
                          }
                        />
                      ))}
                    </span>
                    {referralCount >= FOUNDING_MEMBER_GOAL
                      ? 'You did it — Founding Member status is yours.'
                      : `${Math.min(referralCount, FOUNDING_MEMBER_GOAL)} of ${FOUNDING_MEMBER_GOAL} friends joined`}
                  </p>
                )}

                {signup?.shareUrl && (
                  <>
                    <div className="prelaunch-share-link">
                      <input
                        aria-label="Your personal invite link"
                        onFocus={(event) => event.target.select()}
                        readOnly
                        value={signup.shareUrl}
                      />
                      <button type="button" onClick={copyShareUrl}>
                        {copied ? <Check size={15} /> : <Copy size={15} />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="prelaunch-share-actions">
                      <a
                        className="prelaunch-share-whatsapp"
                        href={`https://wa.me/?text=${encodeURIComponent(`${shareMessage} ${signup.shareUrl}`)}`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <WhatsAppIcon />
                        Share on WhatsApp
                      </a>
                      {typeof navigator !== 'undefined' && !!navigator.share && (
                        <button
                          className="prelaunch-share-native"
                          type="button"
                          onClick={openNativeShare}
                        >
                          <Share2 size={16} />
                          More ways to share
                        </button>
                      )}
                    </div>
                  </>
                )}

                <p className="prelaunch-success__note">
                  We’ll email you the moment Hebrew Hearts opens in {signup?.cityName || 'your city'} —
                  and every friend who joins moves you closer.
                </p>
                <button
                  className="prelaunch-text-button"
                  type="button"
                  onClick={resetForAnotherPerson}
                >
                  Add another person
                </button>
              </div>
            ) : (
              <>
                <p className="prelaunch-card__kicker">Join your city’s founding list</p>
                <h2>Be there from the beginning.</h2>
                <p className="prelaunch-card__intro">
                  No profile yet. Just your place in line and the city you want us to open next.
                </p>

                <div className="prelaunch-field-row">
                  <label className="prelaunch-field">
                    <span>First name</span>
                    <input
                      autoComplete="given-name"
                      maxLength={60}
                      name="firstName"
                      onChange={updateField('firstName')}
                      placeholder="Maya"
                      value={form.firstName}
                    />
                  </label>
                  <label className="prelaunch-field">
                    <span>Email</span>
                    <input
                      autoComplete="email"
                      inputMode="email"
                      maxLength={254}
                      name="email"
                      onChange={updateField('email')}
                      placeholder="maya@example.com"
                      type="email"
                      value={form.email}
                    />
                  </label>
                </div>

                <label className="prelaunch-field">
                  <span>Your city</span>
                  <div className="prelaunch-select-wrap">
                    <MapPin size={18} aria-hidden="true" />
                    <select
                      aria-describedby="city-help"
                      disabled={launchStatusLoading}
                      name="city"
                      onChange={handleCitySelect}
                      value={city}
                    >
                      <option value="">Choose a city</option>
                      {markets.map((market) => (
                        <option key={market.slug} value={market.slug}>
                          {market.name}, {market.country}
                          {market.status === 'launched' ? ' — Now live' : ''}
                        </option>
                      ))}
                      <option value={OTHER_MARKET_SLUG}>Another city</option>
                    </select>
                  </div>
                  <small id="city-help">Choose a live city to enter Hebrew Hearts now.</small>
                </label>

                {city === OTHER_MARKET_SLUG && (
                  <label className="prelaunch-field prelaunch-field--reveal">
                    <span>City, state/province, country</span>
                    <input
                      autoComplete="address-level2"
                      maxLength={120}
                      name="customCity"
                      onChange={updateField('customCity')}
                      placeholder="Philadelphia, PA, United States"
                      value={form.customCity}
                    />
                  </label>
                )}

                <div className="prelaunch-field-row prelaunch-field-row--even">
                  <label className="prelaunch-field">
                    <span>I am a</span>
                    <select
                      className="prelaunch-select-plain"
                      name="gender"
                      onChange={updateField('gender')}
                      value={form.gender}
                    >
                      <option value="">Choose one</option>
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="prelaunch-field">
                    <span>Age range</span>
                    <select
                      className="prelaunch-select-plain"
                      name="ageBracket"
                      onChange={updateField('ageBracket')}
                      value={form.ageBracket}
                    >
                      <option value="">Choose one</option>
                      {AGE_BRACKET_OPTIONS.map((bracket) => (
                        <option key={bracket} value={bracket}>
                          {bracket}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="prelaunch-honeypot" aria-hidden="true">
                  Website
                  <input
                    autoComplete="off"
                    name="website"
                    onChange={updateField('website')}
                    tabIndex={-1}
                    value={form.website}
                  />
                </label>

                <label className="prelaunch-consent">
                  <input
                    checked={form.consent}
                    name="consent"
                    onChange={updateField('consent')}
                    type="checkbox"
                  />
                  <span className="prelaunch-consent__box"><Check size={13} /></span>
                  <span>
                    Email me launch news and early-access invitations. I can unsubscribe anytime.
                    {' '}<Link to="/privacy-policy">Privacy policy</Link>
                  </span>
                </label>

                <TurnstileWidget
                  key={turnstileKey}
                  onToken={handleTurnstileToken}
                  onUnavailable={handleTurnstileUnavailable}
                />

                {status.type === 'error' && (
                  <p className="prelaunch-form-error" role="alert">{status.message}</p>
                )}

                <button
                  className="prelaunch-submit"
                  disabled={status.type === 'submitting'}
                  type="submit"
                >
                  {status.type === 'submitting' ? 'Saving your place…' : 'Join the founding list'}
                  {status.type !== 'submitting' && <ArrowRight size={18} />}
                </button>
                <p className="prelaunch-card__fineprint">Free to join. 18+ only. No spam, ever.</p>
              </>
            )}
          </form>
        </div>
      </section>

      <section className="prelaunch-markets" aria-labelledby="market-status-title">
        <div className="prelaunch-section-heading">
          <p className="prelaunch-eyebrow">City by city</p>
          <h2 id="market-status-title">A stronger community, from day one.</h2>
          <p>
            We launch where there’s enough local momentum to make every introduction count.
          </p>
        </div>

        <div className="prelaunch-market-grid">
          <article className="prelaunch-market-panel prelaunch-market-panel--live">
            <div className="prelaunch-market-panel__heading">
              <span className="prelaunch-status-dot" />
              <div>
                <p>Now live</p>
                <h3>{launchedMarkets.length ? 'Ready to meet' : 'Opening soon'}</h3>
              </div>
            </div>
            {launchedMarkets.length ? (
              <ul>
                {launchedMarkets.map((market) => (
                  <li key={market.slug}>
                    <span>{market.name}</span>
                    <strong>Live</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="prelaunch-market-panel__empty">
                The first founding city will be announced here.
              </p>
            )}
          </article>

          <article className="prelaunch-market-panel">
            <div className="prelaunch-market-panel__heading">
              <span className="prelaunch-status-icon"><Users size={18} /></span>
              <div>
                <p>Building now</p>
                <h3>Founding communities</h3>
              </div>
            </div>
            <ul>
              {buildingMarkets.map((market) => (
                <li key={market.slug}>
                  <span>{market.name}</span>
                  <button
                    type="button"
                    className="prelaunch-market-join"
                    onClick={() => joinCity(market.slug)}
                  >
                    Join
                  </button>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="prelaunch-steps" aria-labelledby="how-launch-works">
        <div className="prelaunch-section-heading">
          <p className="prelaunch-eyebrow">The founding circle</p>
          <h2 id="how-launch-works">Your city’s story starts with you.</h2>
        </div>
        <div className="prelaunch-step-grid">
          <article>
            <span>01</span>
            <Heart size={25} />
            <h3>Raise your hand</h3>
            <p>Join the list for your city in less than a minute.</p>
          </article>
          <article>
            <span>02</span>
            <Users size={25} />
            <h3>Build momentum</h3>
            <p>We grow a balanced, intentional community before opening the doors.</p>
          </article>
          <article>
            <span>03</span>
            <Sparkles size={25} />
            <h3>Meet from day one</h3>
            <p>Founding members hear first when Hebrew Hearts goes live locally.</p>
          </article>
        </div>
        <p className="prelaunch-member-link">
          Already part of the beta? <Link to="/login">Sign in to Hebrew Hearts</Link>
        </p>
      </section>
    </main>
  );
}
